<script setup lang="ts">
  import { Modal } from 'bootstrap'
  import IconComponent from '../../../components/Atoms/Icon.vue'
  import CheckBox from '../components/Atoms/CheckBox.vue'

  import {
    checkIfPluginIsInstalled,
    createPluginConnection,
  } from '../services/pluginHttps'
  import { useModalControl } from '../store/pinia/useModalControl.store'
  import usePluginNotExecutingModal from '../composables/usePluginNotExecutingModal'

  import { ref, onMounted, computed } from 'vue'

  const emit = defineEmits(['retryCertificateIssuing'])
  const modal = ref<InstanceType<typeof Modal> | null>(null)
  const modalControlStore = useModalControl()
  const { showPluginNotExecutingModal, hidePluginNotExecutingModal } =
    usePluginNotExecutingModal()

  const pluginExecutionChecker = ref<any>(null)
  const pluginExecutionUserConfirmation = ref(false)
  const pluginExecution = ref(false)

  const canProceed = computed(() => pluginExecutionUserConfirmation.value)

  onMounted(() => {
    modal.value = new Modal('#modal-plugin-execution-confirmation', {
      backdrop: 'static',
      keyboard: false,
    })
  })

  function clearPluginExecutionChecker() {
    clearInterval(pluginExecutionChecker.value)
  }

  function hide() {
    clearPluginExecutionChecker()
    pluginExecutionUserConfirmation.value = false
    pluginExecution.value = false
    modal.value?.hide()
  }

  function setPluginExecutionChecker() {
    let waitingPromiseResolve = false

    pluginExecutionChecker.value = setInterval(async () => {
      try {
        if (waitingPromiseResolve) {
          return
        }

        waitingPromiseResolve = true

        await createPluginConnection()

        const isPluginInstalled = await checkIfPluginIsInstalled()

        if (isPluginInstalled) {
          pluginExecution.value = true
          clearPluginExecutionChecker()
          emit('retryCertificateIssuing')
          hide()
        }
      } catch (error) {
        pluginExecution.value = false
      }

      waitingPromiseResolve = false
    }, 1000)
  }

  function show() {
    setPluginExecutionChecker()
    modal.value?.show()
  }

  async function fakeCheck() {
    if (!pluginExecution.value) {
      modalControlStore.show({
        modalId: 'pluginNotExecutingModal',
        show: showPluginNotExecutingModal,
        hide: hidePluginNotExecutingModal,
      })
    }

    hide()
  }

  defineExpose({ show, hide })
</script>

<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    id="modal-plugin-execution-confirmation"
    class="modal modal-md fade"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body py-3">
          <img alt="sucesso" src="../assets/computer-with-clock.svg" />

          <p class="fs-6 text-center mt-4">
            <strong>Agora precisamos de você! Clique duas vezes</strong>
            no arquivo baixado <strong>para executá-lo</strong>.
          </p>

          <check-box
            v-model="pluginExecutionUserConfirmation"
            label="Executei o plugin no meu computador."
            type="success"
          />

          <p class="fs-6 text-center mt-4">
            <strong>Não sabe executar o plugin?</strong>

            <a
              href="../faq/digital-certificate-plugin"
              target="_blank"
              rel="noopener noreferrer"
              class="link ms-2"
            >
              <span class="me-2">Clique aqui</span>
              <icon-component name="up-right-from-square" />
            </a>
          </p>
        </div>
        <div class="modal-footer py-3">
          <button
            :class="[
              'btn',
              { 'btn-primary': canProceed },
              { 'btn-secondary': !canProceed },
            ]"
            type="button"
            :disabled="!canProceed"
            @click="fakeCheck"
          >
            Avançar
            <icon-component name="arrow-right" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .modal-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #2f3242;
  }

  .green {
    color: #98dd63;
  }

  .link {
    font-size: 1rem;
    font-weight: 400;
    color: #0080a3;
    cursor: pointer;
  }

  .link:hover {
    text-decoration: underline;
  }
</style>
